import React, { useState } from 'react';
import {
    Drawer, Box, Typography, IconButton, Tabs, Tab, Table, TableHead, TableBody,
    TableRow, TableCell, CircularProgress, Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import { AuditLogExplorerTypes } from '../../types/auditLogExplorer.types';
import { statusText } from '../../builders/audit-log-explorer-builders';
import { JsonView } from './json-view';
import { Badge, FlagChip, MethodBadge, InfoBadge, NeutralBadge } from './audit-badges';
import { ALE, statusBadgeColors } from './tokens';
import { dbDuration, extDuration, formatMs, responseTimeTone } from './helpers';

type Detail = AuditLogExplorerTypes.DetailRow;

const copy = (text: string) => { try { navigator.clipboard?.writeText(text); } catch { /* noop */ } };

const Copyable: React.FC<{ value: string }> = ({ value }) => (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
        <Box component="span" sx={{ fontFamily: ALE.mono, fontSize: '12.5px', color: ALE.tSecondary }}>{value}</Box>
        <Tooltip title="Copy">
            <IconButton size="small" onClick={() => copy(value)} sx={{ p: '2px', color: ALE.g500 }}>
                <ContentCopyIcon sx={{ fontSize: 13 }} />
            </IconButton>
        </Tooltip>
    </Box>
);

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Typography sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '.6px', textTransform: 'uppercase', color: ALE.tFaint, mt: '22px', mb: '9px' }}>
        {children}
    </Typography>
);

const GentleNote: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px', bgcolor: ALE.g100, border: `1px dashed ${ALE.g300}`, borderRadius: '8px', p: '14px 16px', color: ALE.tMuted, fontSize: '13px' }}>
        {children}
    </Box>
);

/** Key/value grid for the Overview pane. */
const OverviewGrid: React.FC<{ rows: [string, React.ReactNode][] }> = ({ rows }) => (
    <Box sx={{ display: 'grid', gridTemplateColumns: '148px 1fr', border: `1px solid ${ALE.border}`, borderRadius: '8px', overflow: 'hidden' }}>
        {rows.map(([k, v], i) => (
            <React.Fragment key={k}>
                <Box sx={{ bgcolor: ALE.headerStrip, fontSize: '12px', fontWeight: 600, color: ALE.tMuted, p: '11px 14px', borderBottom: i < rows.length - 1 ? `1px solid ${ALE.border}` : 'none' }}>{k}</Box>
                <Box sx={{ fontSize: '13px', color: ALE.tPrimary, p: '11px 14px', borderBottom: i < rows.length - 1 ? `1px solid ${ALE.border}` : 'none', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>{v}</Box>
            </React.Fragment>
        ))}
    </Box>
);

const Mono: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Box component="span" sx={{ fontFamily: ALE.mono, fontSize: '12.5px', color: ALE.tSecondary }}>{children}</Box>
);

function OverviewPane({ r }: { r: Detail }) {
    const tone = responseTimeTone(r.responseTimeMs);
    const respColor = tone === 'vslow' ? ALE.errorT : tone === 'slow' ? ALE.warnT : ALE.tSecondary;
    const { bg, fg } = statusBadgeColors(r.statusCode);
    return (
        <Box>
            <OverviewGrid rows={[
                ['Record ID', <Copyable value={r.mongoId} />],
                ['Status', <Badge bg={bg} fg={fg}>{statusText(r.statusCode)}</Badge>],
                ['Response time', <Box component="span" sx={{ color: respColor, fontWeight: 600 }}>{r.responseTimeMs.toLocaleString()} ms</Box>],
                ['Endpoint', <Mono>{r.endpoint}</Mono>],
                ['Path', <Mono>{r.path}</Mono>],
                ['Organization', r.subscriptionId ? <Copyable value={r.subscriptionId} /> : <span style={{ color: ALE.tFaint }}>—</span>],
                ['User', r.userId ? <Mono>{r.userId}</Mono> : <span style={{ color: ALE.tFaint }}>—</span>],
                ['User type', r.userType ? <NeutralBadge>{r.userType}</NeutralBadge> : <span style={{ color: ALE.tFaint }}>—</span>],
                ['API source', <Mono>{r.apiSource}</Mono>],
                ['Environment', <InfoBadge>{r.environment}</InfoBadge>],
                ['User agent', <Mono>{r.userAgent || '—'}</Mono>],
                ['Client IP', r.clientIp ? <><Copyable value={r.clientIp} /> <NeutralBadge>hashed</NeutralBadge></> : <span style={{ color: ALE.tFaint }}>—</span>],
                ['Sampling', r.sampled
                    ? <InfoBadge>sampled · {r.sampleRate ?? '?'}%</InfoBadge>
                    : <FlagChip on={false} label="full capture" />],
                ['Flags', <Box sx={{ display: 'flex', gap: '6px' }}><FlagChip on={!!r.isPomMaster} label="isPomMaster" /><FlagChip on={!!r.isMasquarade} label="isMasquarade" /></Box>],
            ]} />
            {r.errorMessage && (
                <>
                    <SectionLabel>Error</SectionLabel>
                    <JsonView value={{ status: r.statusCode, message: r.errorMessage, retryable: r.statusCode >= 500 }} />
                </>
            )}
        </Box>
    );
}

// `truncated` is typed boolean in the contract but arrives as 0/1 from MySQL — keep it loose so the `!!` coercion is honest.
function BodyPane({ body, queryParams, truncated, kind }: { body: unknown; queryParams?: unknown; truncated?: boolean | number | null; kind: string }) {
    if (body == null && queryParams == null) {
        return <GentleNote>No {kind.toLowerCase()} body captured for this request (auth bodies are redacted; some paths are not audited).</GentleNote>;
    }
    return (
        <Box>
            {!!truncated && <Box sx={{ mb: '9px' }}><Badge bg={ALE.warnBg} fg={ALE.warnT}>truncated · ~10KB cap</Badge></Box>}
            {queryParams != null && (
                <>
                    <SectionLabel>Query params</SectionLabel>
                    <JsonView value={queryParams} />
                </>
            )}
            {body != null && (
                <>
                    {queryParams != null && <SectionLabel>Body</SectionLabel>}
                    <JsonView value={body} />
                </>
            )}
        </Box>
    );
}

function DbOpsPane({ r }: { r: Detail }) {
    const ops = r.dbOperations || [];
    if (!ops.length) return <GentleNote>No DB ops captured (may be a non-audited path).</GentleNote>;
    return (
        <Table sx={{ border: `1px solid ${ALE.border}`, borderRadius: '8px' }}>
            <TableHead>
                <TableRow>
                    {['Collection', 'Operation', 'ms', 'Success'].map((h, i) => (
                        <TableCell key={h} align={i === 2 ? 'right' : 'left'} sx={{ bgcolor: ALE.headerStrip, fontSize: '10.5px', textTransform: 'uppercase', letterSpacing: '.4px', color: ALE.tMuted, fontWeight: 600, p: '9px 12px' }}>{h}</TableCell>
                    ))}
                </TableRow>
            </TableHead>
            <TableBody>
                {ops.map((o, i) => (
                    <TableRow key={i}>
                        <TableCell sx={{ p: '9px 12px', fontFamily: ALE.mono, fontSize: '12px', color: ALE.tPrimary }}>{o.collection ?? '—'}</TableCell>
                        <TableCell sx={{ p: '9px 12px', color: ALE.tSecondary, fontSize: '12.5px' }}>{o.operation ?? '—'}</TableCell>
                        <TableCell align="right" sx={{ p: '9px 12px', color: ALE.tSecondary, fontSize: '12.5px' }}>{dbDuration(o) ?? '—'}</TableCell>
                        <TableCell sx={{ p: '9px 12px' }}>
                            {o.success === false
                                ? <Badge bg={ALE.errorBg} fg={ALE.errorT}>failed</Badge>
                                : <Badge bg={ALE.successBg} fg={ALE.successT}>ok</Badge>}
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
}

function ExternalPane({ r }: { r: Detail }) {
    const calls = r.externalApiCalls || [];
    if (!calls.length) return <GentleNote>No external API calls for this request.</GentleNote>;
    return (
        <Box>
            {calls.map((x, i) => {
                const code = typeof x.status === 'number' ? x.status : 0;
                const { bg, fg } = statusBadgeColors(code || 200);
                const dur = extDuration(x);
                return (
                    <Box key={i} sx={{ border: `1px solid ${ALE.border}`, borderRadius: '8px', p: '13px 15px', mb: '10px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px', mb: '8px' }}>
                            <Typography sx={{ fontWeight: 600, fontSize: '13.5px', color: ALE.tPrimary }}>{x.service ?? 'external'}</Typography>
                            {code ? <Badge bg={bg} fg={fg}>{code}</Badge> : null}
                        </Box>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px 18px', fontSize: '12.5px', color: ALE.tMuted }}>
                            <span><b style={{ color: ALE.tSecondary }}>{x.method ?? ''}</b> {x.endpoint ?? ''}</span>
                            {x.retries != null && <span>retries: <b style={{ color: ALE.tSecondary }}>{x.retries}</b></span>}
                            {dur != null && <span>latency: <b style={{ color: ALE.tSecondary }}>{formatMs(dur)}</b></span>}
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
}

function TimingPane({ r }: { r: Detail }) {
    const dbColors = [ALE.pMain, ALE.pDark, ALE.pLight, ALE.pDarker];
    const extColors = [ALE.errorT, ALE.warnT, '#FF7A59'];
    const segs: { name: string; ms: number; color: string }[] = [];
    (r.dbOperations || []).forEach((o, i) => { const ms = dbDuration(o); if (ms) segs.push({ name: `${o.collection ?? 'db'}.${o.operation ?? 'op'}`, ms, color: dbColors[i % dbColors.length] }); });
    (r.externalApiCalls || []).forEach((x, i) => { const ms = extDuration(x); if (ms) segs.push({ name: x.service ?? 'external', ms, color: extColors[i % extColors.length] }); });
    const used = segs.reduce((s, x) => s + x.ms, 0);
    const other = Math.max(r.responseTimeMs - used, 0);
    segs.push({ name: 'App / other', ms: Number(other.toFixed(1)), color: ALE.g400 });
    const total = r.responseTimeMs || 1;
    return (
        <Box>
            <Box sx={{ display: 'flex', height: 28, borderRadius: '7px', overflow: 'hidden', border: `1px solid ${ALE.border}`, mb: '16px' }}>
                {segs.map((s, i) => <Box key={i} sx={{ width: `${(s.ms / total) * 100}%`, bgcolor: s.color }} />)}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {segs.map((s, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '12.5px' }}>
                        <Box sx={{ width: 11, height: 11, borderRadius: '3px', bgcolor: s.color, flex: '0 0 11px' }} />
                        <Box component="span" sx={{ color: ALE.tSecondary, fontWeight: 500 }}>{s.name}</Box>
                        <Box component="span" sx={{ width: 46, textAlign: 'right', color: ALE.tFaint, fontSize: '11.5px' }}>{((s.ms / total) * 100).toFixed(0)}%</Box>
                        <Box component="span" sx={{ ml: 'auto', width: 74, textAlign: 'right', fontWeight: 600, color: ALE.tSecondary }}>{s.ms.toLocaleString()} ms</Box>
                    </Box>
                ))}
            </Box>
            <SectionLabel>Total wall time</SectionLabel>
            <Typography sx={{ fontSize: 22, fontWeight: 700, color: ALE.tPrimary, fontVariantNumeric: 'tabular-nums' }}>{r.responseTimeMs.toLocaleString()} ms</Typography>
        </Box>
    );
}

const TABS = ['Overview', 'Request', 'Response', 'DB Ops', 'External', 'Timing', 'Raw'] as const;

export interface AuditDetailDrawerProps {
    open: boolean;
    loading: boolean;
    detail: Detail | null;
    onClose: () => void;
}

export const AuditDetailDrawer: React.FC<AuditDetailDrawerProps> = ({ open, loading, detail, onClose }) => {
    const [tab, setTab] = useState(0);
    // Reset to Overview whenever a new record loads.
    React.useEffect(() => { if (detail) setTab(0); }, [detail?.mongoId]);

    // Counts for the DB Ops / External tabs — rendered only when positive.
    const dbCount = detail?.dbOperations?.length ?? detail?.totalDbOperations ?? 0;
    const extCount = detail?.externalApiCalls?.length ?? detail?.totalExternalApiCalls ?? 0;

    return (
        <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: 640, maxWidth: '94vw', boxShadow: ALE.shadowDrawer } }}>
            <Box sx={{ p: '18px 22px 0' }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: '11px', fontWeight: 600, letterSpacing: '.5px', textTransform: 'uppercase', color: ALE.tFaint }}>Audit record</Typography>
                        <Box sx={{ fontSize: '17px', fontWeight: 600, color: ALE.tPrimary, mt: '3px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                            Request <Box component="span" sx={{ fontFamily: ALE.mono, fontSize: '14.5px' }}>{detail ? `${detail.mongoId.slice(0, 6)}…` : '…'}</Box>
                            {detail && <MethodBadge method={detail.method} />}
                            {detail && <Box component="span" sx={{ fontFamily: ALE.mono, fontSize: '14.5px', color: ALE.tSecondary }}>{detail.path}</Box>}
                        </Box>
                    </Box>
                    <IconButton onClick={onClose} sx={{ ml: 'auto', width: 34, height: 34, borderRadius: '8px', border: `1px solid ${ALE.border}`, color: ALE.g600 }}>
                        <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>
                <Tabs
                    value={tab}
                    onChange={(_, v) => setTab(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ mt: '16px', minHeight: 'auto', borderBottom: `1px solid ${ALE.border}`, '& .MuiTabs-indicator': { backgroundColor: ALE.pMain } }}
                >
                    {TABS.map((t) => {
                        const count = t === 'DB Ops' ? dbCount : t === 'External' ? extCount : undefined;
                        return (
                            <Tab
                                key={t}
                                disableRipple
                                label={
                                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                                        {t}
                                        {!!count && (
                                            <Box component="span" sx={{ minWidth: 18, height: 18, px: '5px', borderRadius: '9px', bgcolor: ALE.g200, color: ALE.tMuted, fontSize: '10.5px', fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{count}</Box>
                                        )}
                                    </Box>
                                }
                                sx={{ textTransform: 'none', fontSize: '13px', fontWeight: 600, color: ALE.tMuted, minHeight: 'auto', p: '10px 13px', '&.Mui-selected': { color: ALE.pMain } }}
                            />
                        );
                    })}
                </Tabs>
            </Box>

            <Box sx={{ flex: 1, overflowY: 'auto', p: '20px 22px 40px' }}>
                {loading || !detail ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: '60px', gap: 2, color: ALE.tMuted }}>
                        <CircularProgress size={30} sx={{ color: ALE.pMain }} />
                        <Typography sx={{ fontSize: 13 }}>Loading record…</Typography>
                    </Box>
                ) : (
                    <>
                        {tab === 0 && <OverviewPane r={detail} />}
                        {tab === 1 && <BodyPane body={detail.requestBody} queryParams={detail.queryParams} truncated={detail.requestBodyTruncated} kind="Request" />}
                        {tab === 2 && <BodyPane body={detail.responseBody} truncated={detail.responseBodyTruncated} kind="Response" />}
                        {tab === 3 && <DbOpsPane r={detail} />}
                        {tab === 4 && <ExternalPane r={detail} />}
                        {tab === 5 && <TimingPane r={detail} />}
                        {tab === 6 && <JsonView value={detail} />}
                    </>
                )}
            </Box>
        </Drawer>
    );
};
